"use client"

import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { ChevronLeft, ChevronRight } from "lucide-react"
import BannerImg1 from "../images/img-1.jpg"
import BannerImg2 from "../images/img-2.jpg"
import BannerImg3 from "../images/img-3.jpg"
const 
HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  // Define slides with actual images
  const slides = [
    {
      id: 2,
      //  title: "Advanced Medical Care",
      // subtitle: "Where Technology Meets Compassion, and Patients Always Come First.",
      //  description:
      // "We're committed to the health and wellbeing of your entire family, providing compassionate care at every stage of life.",
      image: "https://mamshospitals.com//Banner1.jpeg",
      ctaText: "Meet Our Doctors",
      ctaLink: "/doctors",
      ctaSecondaryText: "Contact Us",
      ctaSecondaryLink: "/contact",
      mobileImage: "https://mamshospitals.com//MAMS_Mobile Web Banner-4.png",
      },
    {
      id: 5,
      image: "https://mamshospitals.com//MAMS_Banner_4.jpg",
      ctaText: "Our Departments",
      ctaLink: "/departments",
      ctaSecondaryText: "Contact Us",
      ctaSecondaryLink: "/contact",
      mobileImage: "https://mamshospitals.com//MAMS_Mobile Web Banner-2.png",
    },
    {
      id: 6,
      image: "https://mamshospitals.com//MAMS_Banner_5.jpg",
      ctaText: "Meet Our Doctors",
      ctaLink: "/doctors",
      ctaSecondaryText: "Contact Us",
      ctaSecondaryLink: "/contact",
      mobileImage: "https://mamshospitals.com//MAMS_Mobile Web Banner-3.png",
    },
    {
      id: 7,
      image: "https://mamshospitals.com//MAMS_Banner_3.jpg",
      ctaText: " Know About Us",
      ctaLink: "/about",
      ctaSecondaryText: "Contact Us",
      ctaSecondaryLink: "/contact",
      mobileImage: "https://mamshospitals.com//MAMS_Mobile Web Banner-1.png",
    },
    {
      id: 9,
      image: "https://mamshospitals.com//MAMS_Banner_2.jpg",
      ctaText: "Meet Our Doctors",
      ctaLink: "/doctors",
      ctaSecondaryText: "Contact Us",
      ctaSecondaryLink: "/contact",
      mobileImage: "https://mamshospitals.com//MAMS_Mobile Web Banner-5.png",
    },
  ]

  // Function to go to next slide
  const nextSlide = useCallback(() => {
    try {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1))
    } catch (error) {
      console.error("Error in nextSlide:", error)
    }
  }, [slides.length])

  // Function to go to previous slide
  const prevSlide = useCallback(() => {
    try {
      setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1))
    } catch (error) {
      console.error("Error in prevSlide:", error)
    }
  }, [slides.length])

  // Function to go to a specific slide
  const goToSlide = useCallback(
    (index) => {
      try {
        if (index >= 0 && index < slides.length) {
          setCurrentSlide(index)
        }
      } catch (error) {
        console.error("Error in goToSlide:", error)
      }
    },
    [slides.length],
  )

  // Auto-play functionality
  useEffect(() => {
    setIsLoaded(true)

    if (!isPaused) {
      const interval = setInterval(() => {
        nextSlide()
      }, 500000)

      return () => {
        clearInterval(interval)
      }
    }

    return undefined
  }, [isPaused, nextSlide])

  // Handle errors gracefully
  if (!slides || !Array.isArray(slides) || slides.length === 0) {
    return (
      <div className="relative bg-apple-gray-50 h-[500px] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-apple-gray-500">Welcome to MAMS Hospitals</h2>
          <p className="text-apple-gray-400 mt-2">Where Healing Meets Excellence</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="relative overflow-hidden h-[400px] md:h-[600px] bg-apple-gray-50"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            isLoaded && index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
          aria-hidden={index !== currentSlide}
        >
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0  from-black/50 to-black/30 z-10"></div>

          {/* Image */}
          {slide.id === 1 ? (
              <video
                src={slide.video}
                autoPlay
                loop
                muted
                className="object-cover w-full h-full"
              />
            ) : (
              <>
                {/* Desktop Image */}
                <img
                  src={slide.image || "/placeholder.svg"}
                  alt={slide.title}
                  className="hidden md:block object-cover w-full h-full"
                />
                {/* Mobile Image */}
                <img
                  src={slide.mobileImage || slide.image || "/placeholder.svg"}
                  alt={slide.title}
                  className="block md:hidden object-cover w-full h-full"
                />
              </>
            )}


          {/* Content */}
          <div className="absolute inset-0 flex items-center z-20">
            <div className="relative container mx-auto px-4 text-center flex justify-center h-full">
              <div className="absolute max-w-3xl mx-auto text-white bottom-[90px] md:bottom-[60px] hidden md:block">
                <h2 className="text-lg md:text-xl font-medium mb-2 opacity-90">{slide.title}</h2>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-semibold mb-4">{slide.subtitle}</h1>
                <p className="text-base md:text-lg mb-8 text-white/90 max-w-2xl mx-auto px-5 md:px-0">{slide.description}</p>
                <div className="flex flex-row md:flex flex-row sm:flex-row gap-4 justify-center">
                  <Link to={slide.ctaLink}>
                    <div className="apple-button min-w-32">{slide.ctaText}</div>
                  </Link>
                  <Link to={slide.ctaSecondaryLink}>
                    <div className="apple-button-secondary bg-white/20 hover:bg-white/30 text-white border-white/30 min-w-32">
                      {slide.ctaSecondaryText}
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={(e) => {
          e.preventDefault()
          prevSlide()
        }}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 p-2 rounded-full backdrop-blur-sm transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6 text-white" />
      </button>

      <button
        onClick={(e) => {
          e.preventDefault()
          nextSlide()
        }}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 p-2 rounded-full backdrop-blur-sm transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6 text-white" />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.preventDefault()
              goToSlide(index)
            }}
            className={`w-3 h-3 rounded-full transition-colors ${
              index === currentSlide ? "bg-white" : "bg-white/50 hover:bg-white/70"
            }`}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === currentSlide ? "true" : "false"}
          />
        ))}
      </div>
    </div>
  )
}

export default HeroSlider
